/**
 * Release 2.2B — Deal Workspace (structured).
 * Tabs: Overview / Parties / Commercial / Delivery / Payment / Documents / Activity.
 * Every tab is editable in place with a per-tab dirty guard.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import {
  ArrowLeft, LayoutGrid, Users, ClipboardList, Truck, Landmark, GitCompareArrows,
  FileText, MessageSquare, Save, Archive, ArchiveRestore, Trash2, AlertCircle, Plus, PenTool, FileDown, X, FolderClosed, Send, Reply, AtSign, CheckCircle2, RotateCcw, Share2,
} from "lucide-react";
import { TESTIDS } from "@/constants/testIds";
import FileLibrary from "@/components/FileLibrary";
import ExternalSharingPanel from "@/components/ExternalSharingPanel";
import DealConsistencyPanel from "@/components/DealConsistencyPanel";
import {
  DEAL_STAGES_NEW, DEAL_STATUSES_NEW, PARTY_ROLES,
  dealStageLabelKey, dealStatusLabelKey, dealTypeLabelKey,
  computeMissingInfo, computeActivityEvents,
  INCOTERMS_RULES, INCOTERMS_VERSIONS, PAYMENT_METHODS, CURRENCIES,
  TRANSPORT_MODES, QUANTITY_UNITS,
} from "@/lib/dealsMeta";
import { isDraftOutdated, recommendDocumentPack } from "@/lib/documentPack";
import { useRoomCopy } from "@/locales/rooms";

const TABS = [
  { key: "overview",   icon: LayoutGrid,     labelKey: "ws_tab_overview" },
  { key: "parties",    icon: Users,          labelKey: "ws_tab_parties" },
  { key: "commercial", icon: ClipboardList,  labelKey: "ws_tab_commercial" },
  { key: "delivery",   icon: Truck,          labelKey: "ws_tab_delivery" },
  { key: "payment",    icon: Landmark,       labelKey: "ws_tab_payment" },
  { key: "documents",  icon: FileText,       labelKey: "ws_tab_documents" },
  { key: "consistency", icon: GitCompareArrows, labelKey: "ws_tab_consistency" },
  { key: "files",      icon: FolderClosed,   labelKey: "ws_tab_files" },
  { key: "sharing",    icon: Share2,          labelKey: "ws_tab_sharing" },
  { key: "activity",   icon: MessageSquare,  labelKey: "ws_tab_conversation" },
];

export default function DealDetailPage() {
  const roomCopy = useRoomCopy();
  const [roomsEnabled, setRoomsEnabled] = useState(false);
  useEffect(() => { api.capabilities().then((c) => setRoomsEnabled(c.private_deal_rooms === true)).catch(() => {}); }, []);
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const [deal, setDeal] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, dr, msg, members] = await Promise.all([
        api.getDeal(id), api.listDrafts(id), api.listDealMessages(id), api.listOrganizationMembers(),
      ]);
      setDeal(d); setDrafts(dr); setMessages(msg); setWorkspaceMembers(members);
    } catch { toast.error(t("err_generic_save")); nav("/app/deals"); }
    finally { setLoading(false); }
  }, [id, nav, t]);
  useEffect(() => { load(); }, [load]);

  const patchDeal = async (patch) => {
    try {
      const updated = await api.updateDeal(id, patch);
      setDeal(updated);
      toast.success(t("ok_deal_updated"));
      return updated;
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det === "reference_taken") toast.error(t("err_reference_taken"));
      else toast.error(t("err_generic_save"));
      throw e;
    }
  };

  const doArchive = async () => {
    if (!window.confirm(t("ws_confirm_archive"))) return;
    const d = await api.archiveDeal(id); setDeal(d); toast.success(t("ok_deal_archived"));
  };
  const doRestore = async () => {
    if (!window.confirm(t("ws_confirm_restore"))) return;
    const d = await api.restoreDeal(id); setDeal(d); toast.success(t("ok_deal_restored"));
  };
  const doDelete = async () => {
    if (!window.confirm(t("ws_confirm_delete"))) return;
    try { await api.deleteDeal(id); toast.success(t("ok_deal_deleted")); nav("/app/deals"); }
    catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && det.code === "deal_has_documents") {
        toast.error(t("ws_delete_blocked").replace("{n}", det.linked_count ?? det.drafts_count));
      } else toast.error(t("err_generic_save"));
    }
  };

  if (loading) return <div className="p-12 text-[var(--td-text-2)] font-mono text-xs">{t("d_deals_loading")}</div>;
  if (!deal) return null;

  const stageKey = dealStageLabelKey(deal.stage);
  const statusKey = dealStatusLabelKey(deal.status);
  const typeKey = dealTypeLabelKey(deal.deal_type);
  const missing = computeMissingInfo(deal);

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12" data-testid="deal-workspace">
      <button onClick={() => nav("/app/deals")}
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--td-text-2)] hover:text-[var(--td-gold)] mb-6 transition-colors">
        <ArrowLeft size={13} /> {t("dd_back")}
      </button>

      {/* Header */}
      {roomsEnabled && <Link to={`/app/rooms?deal=${id}`} className="btn-ghost inline-flex items-center gap-2 mb-5"><MessageSquare size={16} />{roomCopy.title}</Link>}
      <div className="mb-8">
        <div className="kicker mb-3">— {deal.reference || t("dw_reference_lbl")}</div>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-3xl md:text-4xl leading-tight mb-2">{deal.title}</h1>
            <div className="font-mono text-[11px] text-[var(--td-text-3)] uppercase tracking-widest">
              {typeKey ? t(typeKey) : deal.deal_type}
            </div>
            {deal.description && <p className="mt-3 text-[var(--td-text-2)] leading-relaxed max-w-3xl">{deal.description}</p>}
          </div>
          <div className="flex flex-col gap-2 w-full sm:w-auto sm:min-w-[180px]">
            <div>
              <label className="td-label">{t("ws_stage_lbl")}</label>
              <select data-testid="ws-stage" value={deal.stage}
                      onChange={(e) => patchDeal({ stage: e.target.value })}
                      className="td-input !py-1.5 !px-3 !text-xs font-mono uppercase">
                {DEAL_STAGES_NEW.map((s) => <option key={s} value={s}>{t(`ds_${s}`)}</option>)}
                {!DEAL_STAGES_NEW.includes(deal.stage) && stageKey && (
                  <option value={deal.stage}>{t(stageKey)}</option>
                )}
              </select>
            </div>
            <div>
              <label className="td-label">{t("ws_status_lbl")}</label>
              <select data-testid="ws-status" value={deal.status}
                      onChange={(e) => patchDeal({ status: e.target.value })}
                      className="td-input !py-1.5 !px-3 !text-xs font-mono uppercase">
                {DEAL_STATUSES_NEW.map((s) => <option key={s} value={s}>{t(`dst_${s}`)}</option>)}
                {!DEAL_STATUSES_NEW.includes(deal.status) && statusKey && (
                  <option value={deal.status}>{t(statusKey)}</option>
                )}
              </select>
            </div>
            <div>
              <label className="td-label">{t("ws_responsible_lbl")}</label>
              <select data-testid="ws-responsible" value={deal.responsible_user_id || ""}
                      onChange={(e) => patchDeal({ responsible_user_id: e.target.value || null })}
                      className="td-input !py-1.5 !px-3 !text-xs">
                <option value="">{t("ws_responsible_none")}</option>
                {workspaceMembers.map((member) => <option key={member.user_id} value={member.user_id}>{member.full_name} · {t(`org_role_${member.role}`)}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Lifecycle actions */}
        <div className="flex flex-wrap gap-2 mt-4">
          {deal.status === "archived" ? (
            <button onClick={doRestore} data-testid="ws-restore"
                    className="btn-ghost inline-flex items-center gap-2 text-xs">
              <ArchiveRestore size={13} /> {t("ws_restore")}
            </button>
          ) : (
            <button onClick={doArchive} data-testid="ws-archive"
                    className="btn-ghost inline-flex items-center gap-2 text-xs">
              <Archive size={13} /> {t("ws_archive")}
            </button>
          )}
          <button onClick={doDelete} data-testid="ws-delete"
                  className="btn-ghost inline-flex items-center gap-2 text-xs !border-red-500/40 !text-red-400/90 hover:!text-red-300">
            <Trash2 size={13} /> {t("ws_delete")}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="hairline-bottom mb-8">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map(({ key, labelKey, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
                    data-testid={`ws-tab-${key}`}
                    className={`flex items-center gap-2 px-4 py-3 font-mono text-[11px] uppercase tracking-widest transition-colors border-b-2 whitespace-nowrap ${
                      tab === key ? "text-[var(--td-gold)] border-[var(--td-gold)]" : "text-[var(--td-text-3)] border-transparent hover:text-[var(--td-text)]"
                    }`}>
              <Icon size={13} /> {t(labelKey)}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview"   && <OverviewTab deal={deal} drafts={drafts} missing={missing} onGoto={setTab} onSave={patchDeal} t={t} />}
      {tab === "parties"    && <PartiesTab deal={deal} draftCount={drafts.length} onSave={patchDeal} t={t} />}
      {tab === "commercial" && <CommercialTab deal={deal} draftCount={drafts.length} onSave={patchDeal} t={t} />}
      {tab === "delivery"   && <DeliveryTab deal={deal} draftCount={drafts.length} onSave={patchDeal} t={t} />}
      {tab === "payment"    && <PaymentTab deal={deal} draftCount={drafts.length} onSave={patchDeal} t={t} />}
      {tab === "documents"  && <DocumentsTab deal={deal} drafts={drafts} onDraftsChange={setDrafts} t={t} />}
      {tab === "consistency" && <DealConsistencyPanel deal={deal} />}
      {tab === "files"      && <FileLibrary dealId={deal.id} />}
      {tab === "sharing"    && <ExternalSharingPanel deal={deal} drafts={drafts} />}
      {tab === "activity"   && <ConversationTab deal={deal} drafts={drafts} messages={messages} setMessages={setMessages} t={t} />}
    </div>
  );
}

// ---------- Overview ----------
function OverviewTab({ deal, drafts, missing, onGoto, onSave, t }) {
  const p = deal.parameters || {};
  const g = p.goods || p.product || {};
  const dely = p.delivery || p.logistics || {};
  const pay = p.payment || {};
  const buyer = deal.parties?.find((x) => x.role === "buyer");
  const seller = deal.parties?.find((x) => x.role === "seller");
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* Summary */}
        <div className="card-surface p-4 sm:p-6">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-2)] mb-4">{t("ws_tab_overview")}</div>
          <div className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
            <Meta label={t("dl_col_buyer")}  value={buyer?.legal_name || deal.counterparty} />
            <Meta label={t("dl_col_seller")} value={seller?.legal_name} />
            <Meta label={t("dw_goods_commodity")} value={g.commodity} />
            <Meta label={t("dw_goods_quantity")} value={g.quantity ? `${g.quantity} ${g.unit || ""}`.trim() : null} />
            <Meta label={t("dw_total_value")} value={deal.total_value != null ? `${deal.currency || ""} ${deal.total_value.toLocaleString()}`.trim() : deal.value} />
            <Meta label={t("dw_delivery_incoterms")} value={dely.incoterms ? `${dely.incoterms}${dely.named_place ? " / " + dely.named_place : ""}` : null} />
            <Meta label={t("dw_delivery_window")} value={dely.shipment_window || dely.shipment_period} />
            <Meta label={t("dw_pay_method")} value={pay.method || pay.payment_instrument} />
          </div>
        </div>

        {/* Next action editor */}
        <NextActionCard deal={deal} onSave={onSave} t={t} />

        {/* Description / internal notes */}
        {(deal.notes || deal.internal_notes) && (
          <div className="card-surface p-4 sm:p-6">
            {deal.notes && <div className="text-[var(--td-text-2)] leading-relaxed whitespace-pre-wrap mb-3">{deal.notes}</div>}
            {deal.internal_notes && (
              <div className="text-xs text-[var(--td-text-3)] italic border-l-2 border-[var(--td-gold-muted)] pl-3 whitespace-pre-wrap">{deal.internal_notes}</div>
            )}
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        <div className="card-surface p-4 sm:p-6 space-y-3">
          <Meta label={t("dw_reference_lbl")} value={deal.reference} mono />
          <Meta label={t("ws_updated_lbl")} value={new Date(deal.updated_at).toLocaleString()} mono />
          <Meta label={t("dd_meta_created")} value={new Date(deal.created_at).toLocaleString()} mono />
          <Meta label={t("ws_tab_documents")} value={`${drafts.length}`} mono />
        </div>

        {/* Missing info */}
        <div className="card-surface p-4 sm:p-6" data-testid="ws-missing-info">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={14} className="text-[var(--td-gold)]" />
            <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-gold)]">{t("mi_title")}</div>
          </div>
          <p className="text-xs text-[var(--td-text-3)] mb-4 leading-relaxed">{t("mi_sub")}</p>
          {missing.length === 0 ? (
            <div className="text-sm text-[var(--td-text-2)]">{t("mi_ok")}</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {missing.map((m, i) => (
                <li key={i} className="flex items-center justify-between gap-3">
                  <span className="text-[var(--td-text-2)]">• {t(m.key)}</span>
                  <button onClick={() => onGoto(m.fixTab)}
                          data-testid={`mi-fix-${m.key}`}
                          className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] hover:underline">
                    {t("mi_edit")} →
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function NextActionCard({ deal, onSave, t }) {
  const na = deal.next_action || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(na);
  useEffect(() => { setForm(deal.next_action || {}); }, [deal.next_action]);

  const save = async () => {
    await onSave({ next_action: form.title ? form : null });
    setEditing(false);
  };
  const clear = async () => { await onSave({ next_action: null }); setEditing(false); };

  return (
    <div className="card-surface p-4 sm:p-6" data-testid="ws-next-action">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-gold)]">{t("na_title")}</div>
        {!editing ? (
          <button onClick={() => setEditing(true)} data-testid="ws-na-edit"
                  className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-2)] hover:text-[var(--td-gold)]">
            {t("na_edit")}
          </button>
        ) : (
          <button onClick={() => { setEditing(false); setForm(deal.next_action || {}); }} className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)] hover:text-[var(--td-text)]">
            {t("ws_cancel_edit")}
          </button>
        )}
      </div>
      {!editing ? (
        na.title ? (
          <div className="text-sm space-y-1">
            <div className="font-serif text-lg leading-tight">{na.title}</div>
            {(na.assignee || na.due_date) && (
              <div className="font-mono text-[11px] text-[var(--td-text-3)] uppercase tracking-widest">
                {na.assignee && <>{na.assignee}</>}
                {na.assignee && na.due_date && " · "}
                {na.due_date && <>{na.due_date}</>}
              </div>
            )}
            {na.note && <p className="text-xs text-[var(--td-text-2)] mt-1 whitespace-pre-wrap">{na.note}</p>}
          </div>
        ) : (
          <div className="text-sm text-[var(--td-text-3)] italic">{t("na_none")}</div>
        )
      ) : (
        <div className="space-y-3">
          <div>
            <label className="td-label">{t("na_action_lbl")}</label>
            <input data-testid="ws-na-title" className="td-input" value={form.title || ""}
                   onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="td-label">{t("na_assignee_lbl")}</label>
              <input className="td-input" value={form.assignee || ""}
                     onChange={(e) => setForm({ ...form, assignee: e.target.value })} />
            </div>
            <div>
              <label className="td-label">{t("na_due_lbl")}</label>
              <input type="date" className="td-input font-mono" value={form.due_date || ""}
                     onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="td-label">{t("na_note_lbl")}</label>
            <textarea rows={2} className="td-input resize-none" value={form.note || ""}
                      onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <div className="flex justify-between">
            <button onClick={clear} className="btn-ghost text-xs">{t("na_clear")}</button>
            <button onClick={save} data-testid="ws-na-save" className="btn-gold inline-flex items-center gap-2 text-xs">
              <Save size={12} /> {t("na_save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Parties ----------
function PartiesTab({ deal, draftCount, onSave, t }) {
  const [parties, setParties] = useState(deal.parties || []);
  const [counterparties, setCounterparties] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setParties(deal.parties || []); setDirty(false); }, [deal.parties]);
  useEffect(() => { api.listCounterparties().then(setCounterparties).catch(() => {}); }, []);

  const upd = (i, patch) => { setParties(parties.map((p, idx) => idx === i ? { ...p, ...patch } : p)); setDirty(true); };
  const add = () => { setParties([...parties, { role: "buyer", counterparty_id: null, legal_name: "" }]); setDirty(true); };
  const remove = (i) => { setParties(parties.filter((_, idx) => idx !== i)); setDirty(true); };
  const save = async () => {
    if (draftCount > 0 && !window.confirm(t("master_change_confirm").replace("{count}", draftCount))) return;
    setSaving(true);
    try { await onSave({ parties }); setDirty(false); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[var(--td-text-2)]">{t("ws_tab_parties")}</p>
        <div className="flex gap-2">
          <button onClick={add} data-testid="ws-add-party" className="btn-ghost inline-flex items-center gap-2 text-xs">
            <Plus size={12} /> {t("dw_add_party")}
          </button>
          <button onClick={save} disabled={!dirty || saving} data-testid="ws-save-parties" className="btn-gold inline-flex items-center gap-2 text-xs disabled:opacity-40">
            <Save size={12} /> {saving ? t("ws_saving") : t("ws_save")}
          </button>
        </div>
      </div>
      {parties.length === 0 ? (
        <div className="card-surface p-12 text-center text-[var(--td-text-2)]">{t("dw_party_empty")}</div>
      ) : (
        <div className="space-y-4">
          {parties.map((p, i) => {
            const cp = counterparties.find((c) => c.id === p.counterparty_id);
            const contacts = cp?.contacts || [];
            return (
              <div key={i} data-testid={`ws-party-${i}`} className="card-surface p-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="td-label">{t("dw_party_role")}</label>
                    <select className="td-input" value={p.role} onChange={(e) => upd(i, { role: e.target.value })}>
                      {PARTY_ROLES.map((r) => <option key={r} value={r}>{t(`pr_${r}`)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="td-label">{t("dw_party_company")}</label>
                    <select className="td-input" value={p.counterparty_id || ""}
                            onChange={(e) => {
                              const id = e.target.value;
                              const c = counterparties.find((x) => x.id === id);
                              upd(i, { counterparty_id: id || null, legal_name: c ? c.legal_name : p.legal_name });
                            }}>
                      <option value="">—</option>
                      {counterparties.map((c) => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
                    </select>
                    {!p.counterparty_id && (
                      <input placeholder={t("dw_new_co_legal")} className="td-input mt-2" value={p.legal_name || ""}
                             onChange={(e) => upd(i, { legal_name: e.target.value })} />
                    )}
                  </div>
                  {contacts.length > 0 && (
                    <div>
                      <label className="td-label">{t("dw_party_contact")}</label>
                      <select className="td-input" value={p.contact_name || ""}
                              onChange={(e) => {
                                const name = e.target.value;
                                const c = contacts.find((x) => x.name === name);
                                upd(i, { contact_name: name || "", contact_email: c?.email || "" });
                              }}>
                        <option value="">{t("dw_party_contact_none")}</option>
                        {contacts.map((c, ci) => <option key={ci} value={c.name}>{c.name}{c.role ? ` — ${c.role}` : ""}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="md:col-span-2 flex items-end gap-3">
                    <div className="flex-1">
                      <label className="td-label">{t("dw_party_notes")}</label>
                      <input className="td-input" value={p.notes || ""} onChange={(e) => upd(i, { notes: e.target.value })} />
                    </div>
                    <button onClick={() => remove(i)} aria-label={t("dw_party_remove")}
                            title={t("dw_party_remove")}
                            className="text-[var(--td-text-3)] hover:text-red-400 p-2 mb-1">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Section editors (Commercial / Delivery / Payment) ----------
function SectionForm({ deal, sectionKey, fallbackKey, fields, onSave, extraLevel, draftCount, t }) {
  const initial = { ...(deal.parameters?.[sectionKey] || deal.parameters?.[fallbackKey] || {}) };
  if (extraLevel) Object.assign(initial, extraLevel(deal));
  const [form, setForm] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const init = { ...(deal.parameters?.[sectionKey] || deal.parameters?.[fallbackKey] || {}) };
    if (extraLevel) Object.assign(init, extraLevel(deal));
    setForm(init);
    setDirty(false);
  }, [deal, sectionKey, fallbackKey]); // eslint-disable-line

  const set = (k, v) => { setForm({ ...form, [k]: v }); setDirty(true); };
  const save = async () => {
    if (draftCount > 0 && !window.confirm(t("master_change_confirm").replace("{count}", draftCount))) return;
    setSaving(true);
    try {
      // extract top-level keys (currency/total_value/total_value_manual) from form
      const topKeys = ["currency", "total_value", "total_value_manual"];
      const topPatch = {};
      const sectionPatch = { ...form };
      topKeys.forEach((k) => {
        if (k in sectionPatch) { topPatch[k] = sectionPatch[k]; delete sectionPatch[k]; }
      });
      if (topPatch.total_value === "" || topPatch.total_value == null) topPatch.total_value = null;
      else if (typeof topPatch.total_value === "string") topPatch.total_value = parseFloat(topPatch.total_value) || null;
      const patch = { parameters: { ...(deal.parameters || {}), [sectionKey]: sectionPatch }, ...topPatch };
      await onSave(patch);
      setDirty(false);
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-end mb-4">
        <button onClick={save} disabled={!dirty || saving}
                data-testid={`ws-save-${sectionKey}`}
                className="btn-gold inline-flex items-center gap-2 text-xs disabled:opacity-40">
          <Save size={12} /> {saving ? t("ws_saving") : t("ws_save")}
        </button>
      </div>
      <div className="card-surface p-6 grid md:grid-cols-2 gap-5">
        {fields.map((f) => (
          <Field key={f.name} f={f} value={form[f.name] ?? ""} onChange={(v) => set(f.name, v)} t={t} />
        ))}
      </div>
    </div>
  );
}

function Field({ f, value, onChange, t }) {
  const wide = f.type === "textarea" || f.wide;
  const label = t(f.labelKey);
  const tid = `ws-field-${f.name}`;
  const input =
    f.type === "select" ? (
      <select className="td-input" data-testid={tid} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {f.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    ) : f.type === "textarea" ? (
      <textarea rows={2} data-testid={tid} className="td-input resize-none" value={value}
                onChange={(e) => onChange(e.target.value)} />
    ) : f.type === "checkbox" ? (
      <label className="inline-flex items-center gap-2 text-sm text-[var(--td-text-2)] mt-2">
        <input type="checkbox" checked={!!value} data-testid={tid}
               onChange={(e) => onChange(e.target.checked)} /> {label}
      </label>
    ) : (
      <input type={f.type || "text"} data-testid={tid} className="td-input"
             inputMode={f.numeric ? "decimal" : undefined}
             value={value} onChange={(e) => onChange(e.target.value)} />
    );
  if (f.type === "checkbox") return <div className={wide ? "md:col-span-2" : ""}>{input}</div>;
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label className="td-label">{label}</label>
      {input}
    </div>
  );
}

function CommercialTab({ deal, draftCount, onSave, t }) {
  const fields = [
    { name: "commodity",         labelKey: "dw_goods_commodity" },
    { name: "origin",            labelKey: "dw_goods_origin" },
    { name: "description",       labelKey: "dw_goods_desc", type: "textarea" },
    { name: "specification",     labelKey: "dw_goods_spec", type: "textarea" },
    { name: "quantity",          labelKey: "dw_goods_quantity", numeric: true },
    { name: "unit",              labelKey: "dw_goods_unit", type: "select", options: QUANTITY_UNITS },
    { name: "tolerance",         labelKey: "dw_goods_tolerance", numeric: true },
    { name: "unit_price",        labelKey: "dw_goods_unit_price", numeric: true },
    { name: "price_basis",       labelKey: "dw_goods_price_basis" },
    { name: "currency",          labelKey: "dw_currency", type: "select", options: CURRENCIES },
    { name: "total_value",       labelKey: "dw_total_value", numeric: true },
    { name: "total_value_manual", labelKey: "dw_total_manual", type: "checkbox" },
    { name: "packaging",         labelKey: "dw_goods_packaging" },
    { name: "inspection",        labelKey: "dw_goods_inspection" },
    { name: "additional_terms",  labelKey: "dw_goods_addl_terms", type: "textarea" },
  ];
  const extra = (d) => ({
    currency: d.currency || "",
    total_value: d.total_value ?? "",
    total_value_manual: d.total_value_manual ?? false,
  });
  return <SectionForm deal={deal} sectionKey="goods" fallbackKey="product" fields={fields} onSave={onSave} extraLevel={extra} draftCount={draftCount} t={t} />;
}

function DeliveryTab({ deal, draftCount, onSave, t }) {
  const fields = [
    { name: "incoterms",           labelKey: "dw_delivery_incoterms", type: "select", options: INCOTERMS_RULES },
    { name: "incoterms_version",   labelKey: "dw_delivery_incoterms_ver", type: "select", options: INCOTERMS_VERSIONS },
    { name: "named_place",         labelKey: "dw_delivery_named_place" },
    { name: "country",             labelKey: "dw_delivery_country" },
    { name: "loading_port",        labelKey: "dw_delivery_load" },
    { name: "discharge_port",      labelKey: "dw_delivery_discharge" },
    { name: "shipment_window",     labelKey: "dw_delivery_window" },
    { name: "transport_mode",      labelKey: "dw_delivery_mode", type: "select", options: TRANSPORT_MODES },
    { name: "partial_allowed",     labelKey: "dw_delivery_partial", type: "checkbox" },
    { name: "transshipment_allowed", labelKey: "dw_delivery_transship", type: "checkbox" },
    { name: "vessel",              labelKey: "dw_delivery_vessel", wide: true },
    { name: "instructions",        labelKey: "dw_delivery_instructions", type: "textarea" },
  ];
  return <SectionForm deal={deal} sectionKey="delivery" fallbackKey="logistics" fields={fields} onSave={onSave} draftCount={draftCount} t={t} />;
}

function PaymentTab({ deal, draftCount, onSave, t }) {
  const fields = [
    { name: "method",                labelKey: "dw_pay_method", type: "select", options: PAYMENT_METHODS },
    { name: "schedule",              labelKey: "dw_pay_schedule" },
    { name: "advance_pct",           labelKey: "dw_pay_advance_pct", numeric: true },
    { name: "balance",               labelKey: "dw_pay_balance" },
    { name: "instrument",            labelKey: "dw_pay_instrument" },
    { name: "issuing_bank",          labelKey: "dw_pay_issuing_bank" },
    { name: "advising_bank",         labelKey: "dw_pay_advising_bank" },
    { name: "amount",                labelKey: "dw_pay_amount", numeric: true },
    { name: "currency",              labelKey: "dw_pay_currency", type: "select", options: CURRENCIES },
    { name: "validity",              labelKey: "dw_pay_validity" },
    { name: "presentation_period",   labelKey: "dw_pay_presentation" },
    { name: "confirmation_required", labelKey: "dw_pay_confirmed", type: "checkbox" },
    { name: "transferable",          labelKey: "dw_pay_transferable", type: "checkbox" },
    { name: "notes",                 labelKey: "dw_pay_notes", type: "textarea" },
  ];
  return <SectionForm deal={deal} sectionKey="payment" fallbackKey="payment" fields={fields} onSave={onSave} draftCount={draftCount} t={t} />;
}

// ---------- Documents ----------
function DocumentsTab({ deal, drafts, onDraftsChange, t }) {
  const [items, setItems] = useState(drafts);
  useEffect(() => { setItems(drafts); }, [drafts]);
  const recommendedPack = useMemo(() => recommendDocumentPack(deal, items), [deal, items]);
  const outdatedCount = useMemo(() => items.filter((draft) => isDraftOutdated(deal, draft)).length, [deal, items]);

  const applyWorkflow = async (draft, action) => {
    let note = null;
    if (action === "request_changes") {
      note = window.prompt(t("doc_workflow_changes_prompt"));
      if (!note?.trim()) return;
    } else if (["approve", "mark_final", "reopen"].includes(action)) {
      const entered = window.prompt(t("doc_workflow_note_prompt"));
      if (entered === null) return;
      note = entered.trim() || null;
    }
    try {
      const updated = await api.updateDraftWorkflow(draft.id, { action, note });
      const nextItems = items.map((item) => item.id === updated.id ? updated : item);
      setItems(nextItems);
      onDraftsChange(nextItems);
      window.dispatchEvent(new Event("td-notifications-changed"));
      toast.success(t("doc_workflow_updated"));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "invalid_document_transition") toast.error(t("doc_workflow_conflict"));
      else if (detail?.code === "stale_deal_master") toast.error(t("master_stale_workflow_blocked"));
      else toast.error(t("err_generic_save"));
    }
  };

  const statusClasses = {
    draft: "border-[var(--td-border)] text-[var(--td-text-2)]",
    in_review: "border-amber-500/50 text-amber-400",
    changes_requested: "border-red-500/50 text-red-400",
    approved: "border-emerald-500/50 text-emerald-400",
    final: "border-blue-400/50 text-blue-300",
  };
  return (
    <div>
      {outdatedCount > 0 && (
        <div className="mb-5 border border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="master-data-impact-warning">
          <AlertCircle size={17} className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-serif text-lg text-amber-200">{t("master_stale_title").replace("{count}", outdatedCount)}</div>
            <p className="text-xs text-amber-100/70 mt-1">{t("master_stale_intro")}</p>
          </div>
        </div>
      )}
      <div className="card-surface p-5 sm:p-6 mb-5" data-testid="recommended-document-pack">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="kicker mb-2">— {t("doc_pack_kicker")}</div>
            <h2 className="font-serif text-xl sm:text-2xl">{t("doc_pack_title")}</h2>
            <p className="text-sm text-[var(--td-text-2)] mt-1 max-w-2xl">{t("doc_pack_intro")}</p>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)] border border-[var(--td-border)] px-2 py-1">
            {recommendedPack.filter((item) => item.draft).length}/{recommendedPack.length} {t("doc_pack_ready")}
          </span>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {recommendedPack.map((recommendation) => (
            <div key={recommendation.code} className="border border-[var(--td-border)] p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs text-[var(--td-gold)]">{recommendation.code}</span>
                  {recommendation.draft && !isDraftOutdated(deal, recommendation.draft) && <CheckCircle2 size={13} className="text-emerald-400" />}
                  {recommendation.draft && isDraftOutdated(deal, recommendation.draft) && <AlertCircle size={13} className="text-amber-400" />}
                </div>
                <div className="text-xs text-[var(--td-text-3)]">{t(recommendation.reasonKey)}</div>
              </div>
              {recommendation.draft ? (
                <Link to={`/app/drafting?draft=${recommendation.draft.id}`}
                      className={`text-[10px] font-mono uppercase tracking-widest shrink-0 ${
                        isDraftOutdated(deal, recommendation.draft)
                          ? "text-amber-400 hover:text-amber-300"
                          : "text-emerald-400 hover:text-emerald-300"
                      }`}>
                  {t("doc_pack_open")}
                </Link>
              ) : (
                <Link to={`/app/drafting?deal=${deal.id}&code=${recommendation.code}`}
                      className="text-[10px] font-mono uppercase tracking-widest text-[var(--td-gold)] hover:text-[var(--td-gold-hover)] shrink-0">
                  {t("doc_pack_create")}
                </Link>
              )}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[var(--td-text-3)] mt-4">{t("doc_pack_disclaimer")}</p>
      </div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl">{items.length}</h2>
        <Link to={`/app/drafting?deal=${deal.id}`}
              data-testid="ws-docs-new"
              className="btn-gold inline-flex items-center gap-2">
          <PenTool size={13} /> {t("ws_docs_new")}
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="card-surface p-14 text-center text-[var(--td-text-2)]">
          <div className="font-serif text-xl">{t("ws_docs_empty")}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((d) => {
            const status = d.workflow_status || "draft";
            const outdated = isDraftOutdated(deal, d);
            const latest = d.workflow_history?.[d.workflow_history.length - 1];
            return (
            <div key={d.id} data-testid="ws-doc-row" className="card-surface p-5 flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <span className="font-mono text-xs text-[var(--td-gold)]">{d.template_code}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)] border border-[var(--td-border)] px-1.5 py-0.5">v{d.version_number || 1}</span>
                  <span className="font-mono text-[10px] text-[var(--td-text-3)]">{(d.language || "en").toUpperCase()}</span>
                  <span className="font-mono text-[10px] text-[var(--td-text-3)]">{new Date(d.created_at).toLocaleString()}</span>
                  <span data-testid={TESTIDS.documentWorkflowStatus}
                        className={`font-mono text-[9px] uppercase tracking-widest border px-2 py-0.5 ${statusClasses[status] || statusClasses.draft}`}>
                    {t(`doc_status_${status}`)}
                  </span>
                  {outdated && (
                    <span className="font-mono text-[9px] uppercase tracking-widest border border-amber-500/50 text-amber-300 px-2 py-0.5">
                      {t("master_stale_badge")}
                    </span>
                  )}
                </div>
                {d.title && <div className="font-serif text-lg">{d.title}</div>}
                {latest && (
                  <div className="text-[10px] text-[var(--td-text-3)] mt-2 max-w-xl">
                    {latest.actor_name} · {new Date(latest.created_at).toLocaleString()}
                    {latest.note && <span> · {latest.note}</span>}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {(status === "draft" || status === "changes_requested") && (
                  <button onClick={() => applyWorkflow(d, "submit_for_review")} data-testid={TESTIDS.documentSubmitReview}
                          className="btn-ghost !py-1.5 !px-3 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                    <Send size={12} /> {t("doc_action_submit")}
                  </button>
                )}
                {status === "in_review" && (
                  <>
                    <button onClick={() => applyWorkflow(d, "approve")} data-testid={TESTIDS.documentApprove}
                            className="btn-ghost !py-1.5 !px-3 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 !text-emerald-400">
                      <CheckCircle2 size={12} /> {t("doc_action_approve")}
                    </button>
                    <button onClick={() => applyWorkflow(d, "request_changes")} data-testid={TESTIDS.documentRequestChanges}
                            className="btn-ghost !py-1.5 !px-3 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 !text-amber-400">
                      <AlertCircle size={12} /> {t("doc_action_changes")}
                    </button>
                  </>
                )}
                {status === "approved" && (
                  <button onClick={() => applyWorkflow(d, "mark_final")} data-testid={TESTIDS.documentMarkFinal}
                          className="btn-ghost !py-1.5 !px-3 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 !text-blue-300">
                    <CheckCircle2 size={12} /> {t("doc_action_final")}
                  </button>
                )}
                {(status === "approved" || status === "final") && (
                  <button onClick={() => applyWorkflow(d, "reopen")} data-testid={TESTIDS.documentReopen}
                          className="btn-ghost !py-1.5 !px-3 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                    <RotateCcw size={12} /> {t("doc_action_reopen")}
                  </button>
                )}
                <Link to={`/app/drafting?draft=${d.id}`}
                      className="btn-ghost !py-1.5 !px-3 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                  <PenTool size={12} /> {t("ws_docs_edit")}
                </Link>
                <button onClick={() => api.exportDraft(d.id, "docx").then(() => toast.success("Word")).catch(() => toast.error(t("st_export_failed")))}
                        className="btn-ghost !py-1.5 !px-3 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                  <FileDown size={12} /> Word
                </button>
                <button onClick={() => api.exportDraft(d.id, "pdf").then(() => toast.success("PDF")).catch(() => toast.error(t("st_export_failed")))}
                        className="btn-ghost !py-1.5 !px-3 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                  <FileDown size={12} /> PDF
                </button>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

// ---------- Conversation + activity ----------
function ConversationTab({ deal, drafts, messages, setMessages, t }) {
  const emptyForm = { kind: "comment", direction: "outbound", subject: "", sender: "", recipient: "", body: "" };
  const [form, setForm] = useState(emptyForm);
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);

  const mentionOptions = useMemo(() => {
    const values = [];
    (deal.parties || []).forEach((party) => {
      [party.contact_name, party.contact_email, party.legal_name].forEach((value) => {
        const clean = (value || "").trim();
        if (clean && !values.includes(clean)) values.push(clean);
      });
    });
    return values.slice(0, 20);
  }, [deal.parties]);

  const insertMention = (label) => {
    const spacer = form.body && !form.body.endsWith(" ") ? " " : "";
    setForm({ ...form, body: `${form.body}${spacer}@[${label}] ` });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.body.trim()) return;
    setSending(true);
    try {
      const created = await api.createDealMessage(deal.id, {
        ...form,
        parent_id: replyTo?.id || null,
        direction: form.kind === "correspondence" ? form.direction : null,
      });
      setMessages([created, ...messages]);
      setForm(emptyForm);
      setReplyTo(null);
      toast.success(t("conversation_saved"));
    } catch {
      toast.error(t("err_generic_save"));
    } finally {
      setSending(false);
    }
  };

  const remove = async (message) => {
    if (!window.confirm(t("conversation_delete_confirm"))) return;
    try {
      await api.deleteDealMessage(deal.id, message.id);
      setMessages(messages.filter((item) => item.id !== message.id));
      toast.success(t("conversation_deleted"));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "message_has_replies") toast.error(t("conversation_delete_blocked"));
      else toast.error(t("err_generic_save"));
    }
  };

  const events = computeActivityEvents(deal, drafts).map((event, index) => ({
    ...event, streamType: "event", streamId: `event-${index}-${event.at}`,
  }));
  const stream = [
    ...events,
    ...messages.map((message) => ({ ...message, streamType: "message", streamId: message.id, at: message.created_at })),
  ].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  return (
    <div className="max-w-4xl space-y-6" data-testid={TESTIDS.conversationPanel}>
      <form onSubmit={submit} className="card-surface p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-gold)]">{t("conversation_title")}</div>
            <p className="text-xs text-[var(--td-text-3)] mt-1">{t("conversation_sub")}</p>
          </div>
          <select data-testid={TESTIDS.conversationKind} className="td-input !w-auto !py-1.5 text-xs"
                  value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="comment">{t("conversation_kind_comment")}</option>
            <option value="internal_note">{t("conversation_kind_note")}</option>
            <option value="correspondence">{t("conversation_kind_correspondence")}</option>
          </select>
        </div>

        {replyTo && (
          <div className="flex items-center justify-between gap-3 border-l-2 border-[var(--td-gold)] pl-3 text-xs text-[var(--td-text-2)]">
            <span>{t("conversation_replying_to")} {replyTo.author_name}: {replyTo.body.slice(0, 90)}</span>
            <button type="button" onClick={() => setReplyTo(null)} className="text-[var(--td-text-3)] hover:text-[var(--td-text)]"><X size={13} /></button>
          </div>
        )}

        {form.kind === "correspondence" && (
          <div className="grid md:grid-cols-2 gap-3">
            <select className="td-input" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
              <option value="outbound">{t("conversation_outbound")}</option>
              <option value="inbound">{t("conversation_inbound")}</option>
            </select>
            <input className="td-input" placeholder={t("conversation_subject")} value={form.subject}
                   onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            <input className="td-input" placeholder={t("conversation_from")} value={form.sender}
                   onChange={(e) => setForm({ ...form, sender: e.target.value })} />
            <input className="td-input" placeholder={t("conversation_to")} value={form.recipient}
                   onChange={(e) => setForm({ ...form, recipient: e.target.value })} />
          </div>
        )}

        <textarea data-testid={TESTIDS.conversationBody} rows={4} className="td-input resize-y"
                  placeholder={t("conversation_placeholder")} value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })} />

        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <AtSign size={13} className="text-[var(--td-text-3)]" />
            {mentionOptions.length ? mentionOptions.map((label) => (
              <button key={label} type="button" onClick={() => insertMention(label)}
                      className="font-mono text-[9px] px-2 py-1 border border-[var(--td-border)] text-[var(--td-text-2)] hover:border-[var(--td-gold)]">
                {label}
              </button>
            )) : <span className="text-[10px] text-[var(--td-text-3)]">{t("conversation_mentions_empty")}</span>}
          </div>
          <button type="submit" disabled={sending || !form.body.trim()} data-testid={TESTIDS.conversationSend}
                  className="btn-gold inline-flex items-center gap-2 text-xs disabled:opacity-40">
            <Send size={12} /> {sending ? t("ws_saving") : t("conversation_send")}
          </button>
        </div>
      </form>

      <div className="card-surface p-6">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-2)] mb-5">{t("conversation_stream")}</div>
        {stream.length === 0 ? (
          <div className="text-sm text-[var(--td-text-3)] italic">{t("ws_activity_empty")}</div>
        ) : (
          <ol className="space-y-4">
            {stream.map((item) => {
              if (item.streamType === "event") {
                let label = t(item.key);
                if (item.vars) Object.entries(item.vars).forEach(([key, value]) => { label = label.replace(`{${key}}`, value); });
                return (
                  <li key={item.streamId} className="flex items-start gap-3 hairline-bottom pb-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--td-gold)] mt-2 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-base leading-tight">{label}</div>
                      <div className="text-xs text-[var(--td-text-3)] font-mono mt-0.5">{new Date(item.at).toLocaleString()}</div>
                    </div>
                  </li>
                );
              }
              const parent = item.parent_id ? messages.find((message) => message.id === item.parent_id) : null;
              return (
                <li key={item.streamId} data-testid={TESTIDS.conversationRow}
                    className={`hairline-bottom pb-4 border-l-2 pl-4 ${item.kind === "internal_note" ? "border-amber-500/50" : item.kind === "correspondence" ? "border-blue-400/50" : "border-[var(--td-border)]"}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-serif text-base">{item.author_name}</span>
                        <span className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-gold)]">{t(`conversation_kind_${item.kind === "internal_note" ? "note" : item.kind}`)}</span>
                        {item.direction && <span className="font-mono text-[9px] uppercase text-[var(--td-text-3)]">{t(`conversation_${item.direction}`)}</span>}
                      </div>
                      <div className="text-[10px] text-[var(--td-text-3)] font-mono">{new Date(item.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" data-testid={TESTIDS.conversationReply} onClick={() => setReplyTo(item)}
                              className="text-[var(--td-text-3)] hover:text-[var(--td-gold)]" title={t("conversation_reply")}><Reply size={13} /></button>
                      <button type="button" data-testid={TESTIDS.conversationDelete} onClick={() => remove(item)}
                              className="text-[var(--td-text-3)] hover:text-red-400" title={t("conversation_delete")}><Trash2 size={13} /></button>
                    </div>
                  </div>
                  {parent && <div className="text-[10px] text-[var(--td-text-3)] mt-2">↳ {parent.author_name}: {parent.body.slice(0, 100)}</div>}
                  {item.kind === "correspondence" && (
                    <div className="mt-2 text-xs text-[var(--td-text-3)] space-y-0.5">
                      {item.subject && <div><span className="text-[var(--td-text-2)]">{t("conversation_subject")}:</span> {item.subject}</div>}
                      {(item.sender || item.recipient) && <div>{item.sender || "—"} → {item.recipient || "—"}</div>}
                    </div>
                  )}
                  <p className="mt-3 text-sm text-[var(--td-text-2)] whitespace-pre-wrap break-words">{item.body}</p>
                  {!!item.mentions?.length && (
                    <div className="flex gap-2 flex-wrap mt-3">
                      {item.mentions.map((mention) => <span key={mention.label} className="font-mono text-[9px] px-2 py-1 bg-[var(--td-bg)] text-[var(--td-gold)]">@{mention.label}</span>)}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

// ---------- Small helpers ----------
function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="td-label">{label}</div>
      <div className={mono ? "font-mono text-xs text-[var(--td-text)]" : "text-sm text-[var(--td-text)]"}>
        {value ? value : <span className="text-[var(--td-text-3)] italic">—</span>}
      </div>
    </div>
  );
}
